import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { locationService } from '../../../utils/locationService';

const LocationSection = ({ formData, updateFormData, errors }) => {
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // State name to abbreviation mapping for enhanced search
  const stateMapping = {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
    'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
    'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
    'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
    'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
    'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
    'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
    'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
    'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY'
  };

  // Load available locations on component mount
  useEffect(() => {
    const loadLocations = async () => {
      const { data } = await locationService?.getAllLocations();
      if (data) {
        setLocationSuggestions(data);
      }
    };
    loadLocations();
  }, []);

  // Direct pricing fetch for selected suggestions
  const fetchPricingDataForSuggestion = async (suggestion) => {
    setLoading(true);
    try {
      // Use city or zip code for more reliable matching
      const searchTerm = suggestion?.zip_code || `${suggestion?.city}, ${suggestion?.state}`;
      
      const { data, error } = await locationService?.getLocationPricing(searchTerm);
      if (error) {
        console.log('Direct pricing fetch error:', error);
        setPricingData(null);
      } else {
        setPricingData(data);
      }
    } catch (error) {
      console.log('Direct pricing error:', error);
      setPricingData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch comprehensive pricing data when location changes
  useEffect(() => {
    const fetchLocationPricing = async () => {
      const location = formData?.location?.address;
      if (!location?.trim()) {
        setPricingData(null);
        return;
      }

      // Skip if we just set pricing data from suggestion click
      if (formData?.location?.rawData) {
        return; // Pricing already fetched in handleSuggestionClick
      }

      setLoading(true);
      try {
        const { data, error } = await locationService?.getLocationPricing(location);
        if (error) {
          console.log('Location pricing fetch error:', error);
          setPricingData(null);
        } else {
          setPricingData(data);
        }
      } catch (error) {
        console.log('Location pricing error:', error);
        setPricingData(null);
      } finally {
        setLoading(false);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(fetchLocationPricing, 500);
    return () => clearTimeout(timeoutId);
  }, [formData?.location?.address]);

  const handleLocationChange = (value) => {
    updateFormData('location', { ...formData?.location, address: value });
    setShowSuggestions(value?.length > 2);
  };

  const handleSuggestionClick = (suggestion) => {
    // Store both the formatted display and the raw suggestion data
    const locationText = locationService?.formatLocationDisplay(suggestion);
    const locationData = {
      address: locationText,
      rawData: suggestion, // Keep reference to original data for better searching
      city: suggestion?.city,
      state: suggestion?.state,
      zipCode: suggestion?.zip_code
    };
    
    updateFormData('location', { ...formData?.location, ...locationData });
    setShowSuggestions(false);
    
    // Immediately trigger pricing data fetch with clean search terms
    fetchPricingDataForSuggestion(suggestion);
  };

  // Enhanced filtering logic to handle full state names and abbreviations
  const filteredSuggestions = locationSuggestions?.filter(location => {
    const searchTerm = formData?.location?.address?.toLowerCase()?.trim() || '';
    
    // Skip empty search terms
    if (!searchTerm) return false;
    
    // More robust matching for better coverage
    const cityLower = location?.city?.toLowerCase() || '';
    const stateLower = location?.state?.toLowerCase() || '';
    const zipCode = location?.zip_code || '';
    
    // Primary matching strategies - order matters for performance
    
    // 1. Exact state abbreviation match (highest priority for state searches)
    if (stateLower === searchTerm) {
      return true;
    }
    
    // 2. State abbreviation starts with search term
    if (stateLower?.startsWith(searchTerm)) {
      return true;
    }
    
    // 3. Full state name matching using mapping
    const mappedState = stateMapping?.[searchTerm];
    if (mappedState && mappedState === location?.state) {
      return true;
    }
    
    // 4. Partial state name matching - check if full state name starts with search
    for (const [fullStateName, stateAbbrev] of Object.entries(stateMapping)) {
      if (fullStateName?.startsWith(searchTerm) && stateAbbrev === location?.state) {
        return true;
      }
    }
    
    // 5. City name matching
    if (cityLower?.includes(searchTerm) || cityLower?.startsWith(searchTerm)) {
      return true;
    }
    
    // 6. Zip code matching
    if (zipCode && zipCode?.includes(searchTerm)) {
      return true;
    }
    
    return false;
  })?.slice(0, 12); // Increased limit for better state coverage

  const pricingSummary = locationService?.getPricingSummary(pricingData);
  const marketDemographics = locationService?.getMarketDemographics(pricingData?.[0]);
  const seasonalInsights = locationService?.getSeasonalInsights(pricingSummary?.seasonalData);

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
          <Icon name="MapPin" size={20} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-heading font-bold text-foreground">Location Details</h3>
          <p className="text-sm text-muted-foreground">Where will your RV park be located?</p>
        </div>
      </div>
      <div className="relative">
        <Input
          label="Property Location"
          type="text"
          placeholder="Enter your location and choose from Dropdown"
          description="Get comprehensive market data and pricing recommendations"
          value={formData?.location?.address || ''}
          onChange={(e) => handleLocationChange(e?.target?.value)}
          error={errors?.location}
          required
        />

        {/* Enhanced Location Suggestions */}
        {showSuggestions && filteredSuggestions?.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {filteredSuggestions?.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-3 text-left hover:bg-muted focus:bg-muted focus:outline-none transition-colors border-b border-border last:border-b-0"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground">
                      {suggestion?.city}, {suggestion?.state}
                      {suggestion?.zip_code && <span className="text-muted-foreground ml-1">({suggestion?.zip_code})</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {suggestion?.region}
                      {suggestion?.population_est && (
                        <span className="ml-2">
                          Pop: {locationService?.formatNumber(suggestion?.population_est)}
                        </span>
                      )}
                    </div>
                  </div>
                  {suggestion?.suggested_nightly_short_usd && (
                    <div className="text-xs font-medium text-primary ml-2">
                      ${suggestion?.suggested_nightly_short_usd}/night
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Loading State */}
      {loading && (
        <div className="mt-4 p-4 bg-muted rounded-lg">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Loader2" size={16} className="animate-spin" />
            <span>Loading comprehensive market data...</span>
          </div>
        </div>
      )}
      {/* Comprehensive Pricing Information */}
      {!loading && formData?.location?.address && pricingSummary?.hasData && (
        <div className="mt-4 space-y-4">
          {/* Main Pricing Card */}
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-start space-x-2 mb-3">
              <Icon name="TrendingUp" size={16} className="text-green-600 mt-1" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-1">
                  {pricingSummary?.message}
                </p>
                
                {/* Core Pricing Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3 text-xs">
                  <div className="bg-background p-2 rounded">
                    <span className="text-muted-foreground block">Suggested Rate</span>
                    <span className="font-medium text-primary">${pricingSummary?.averageRate}/night</span>
                  </div>
                  <div className="bg-background p-2 rounded">
                    <span className="text-muted-foreground block">Peak Season</span>
                    <span className="font-medium text-green-600">${pricingSummary?.peakRate}/night</span>
                  </div>
                  <div className="bg-background p-2 rounded">
                    <span className="text-muted-foreground block">Off Season</span>
                    <span className="font-medium text-blue-600">${pricingSummary?.offSeasonRate}/night</span>
                  </div>
                  <div className="bg-background p-2 rounded">
                    <span className="text-muted-foreground block">Occupancy</span>
                    <span className="font-medium">{pricingSummary?.occupancyRate}%</span>
                  </div>
                </div>

                {/* Monthly Rate & Peak Months */}
                {pricingSummary?.monthlyRate && (
                  <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                    <div className="bg-background p-2 rounded">
                      <span className="text-muted-foreground block">Monthly Rate</span>
                      <span className="font-medium text-purple-600">${pricingSummary?.monthlyRate}/month</span>
                    </div>
                    <div className="bg-background p-2 rounded">
                      <span className="text-muted-foreground block">Peak Months</span>
                      <span className="font-medium">{pricingSummary?.peakMonths || 'Year-round'}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Market Demographics */}
            {marketDemographics && (
              <div className="border-t border-border pt-3 mb-3">
                <h4 className="text-xs font-medium text-foreground mb-2">Market Demographics</h4>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 text-xs">
                  {marketDemographics?.population && (
                    <div>
                      <span className="text-muted-foreground">Population: </span>
                      <span className="font-medium">{marketDemographics?.population?.formatted}</span>
                    </div>
                  )}
                  {marketDemographics?.medianIncome && (
                    <div>
                      <span className="text-muted-foreground">Median Income: </span>
                      <span className="font-medium">{marketDemographics?.medianIncome?.formatted}</span>
                    </div>
                  )}
                  {marketDemographics?.medianHomePrice && (
                    <div>
                      <span className="text-muted-foreground">Median Home: </span>
                      <span className="font-medium">{marketDemographics?.medianHomePrice?.formatted}</span>
                    </div>
                  )}
                </div>
                <div className="mt-2 text-xs">
                  <span className="text-muted-foreground">Data Confidence: </span>
                  <span className="font-medium">{Math.round((marketDemographics?.confidence || 0.4) * 100)}%</span>
                </div>
              </div>
            )}

            {/* Seasonal Insights */}
            {seasonalInsights && (
              <div className="border-t border-border pt-3 mb-3">
                <h4 className="text-xs font-medium text-foreground mb-2">Seasonal Patterns</h4>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-muted-foreground">Peak Demand: </span>
                    <span className="font-medium text-green-600">
                      {seasonalInsights?.peak?.months?.join(', ')} ({seasonalInsights?.peak?.value}%)
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Seasonal Variation: </span>
                    <span className="font-medium">{seasonalInsights?.variation}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div className="border-t border-border pt-3">
              <h4 className="text-xs font-medium text-foreground mb-1">Market Recommendations</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {pricingSummary?.recommendations}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* No Data State */}
      {!loading && formData?.location?.address && !pricingSummary?.hasData && (
        <div className="mt-4 p-4 bg-muted rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-600 mt-1" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">
                {pricingSummary?.message}
              </p>
              <p className="text-xs text-muted-foreground">
                {pricingSummary?.recommendations}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Default Info Message */}
      {!formData?.location?.address && (
        <div className="mt-4 p-4 bg-muted rounded-lg">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Info" size={16} />
            <span>
              Enter a location to get comprehensive market analysis with demographics, seasonal patterns, and pricing recommendations
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSection;